import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Package, Clock, Truck, CheckCircle, XCircle, MessageCircle,
  Lock, Phone, ShieldCheck, ChevronDown, Send, UserCheck, Loader, AlertCircle
} from "lucide-react";
import API from "../api";
import { useAuth } from "../contexts/AuthContext";
import { formatWhatsApp } from "../utils/whatsapp";

const formatPrice = (p) =>
  new Intl.NumberFormat("fr-SN", { style: "currency", currency: "XOF", minimumFractionDigits: 0 }).format(p);

const STATUS_CONFIG = {
  pending: { label: "En attente", icon: Clock, color: "text-yellow-600 bg-yellow-50 border-yellow-200", desc: "En attente de validation admin" },
  initiated: { label: "Initiée", icon: Clock, color: "text-yellow-600 bg-yellow-50 border-yellow-200", desc: "Votre demande a été enregistrée — effectuez votre paiement" },
  awaiting_payment: { label: "Paiement requis", icon: AlertCircle, color: "text-blue-600 bg-blue-50 border-blue-200", desc: "Payez directement via Wave ou Orange Money" },
  payment_declared: { label: "Paiement déclaré", icon: ShieldCheck, color: "text-teal-600 bg-teal-50 border-teal-200", desc: "Paiement déclaré — en cours de vérification par le vendeur" },
  payment_confirmed: { label: "Paiement confirmé", icon: ShieldCheck, color: "text-green-600 bg-green-50 border-green-200", desc: "Paiement reçu — contact vendeur débloqué !" },
  approved: { label: "Approuvé", icon: ShieldCheck, color: "text-green-600 bg-green-50 border-green-200", desc: "Infos de contact débloquées !" },
  paid: { label: "Payé", icon: CheckCircle, color: "text-blue-600 bg-blue-50 border-blue-200", desc: "" },
  en_livraison: { label: "En livraison", icon: Truck, color: "text-orange-600 bg-orange-50 border-orange-200", desc: "" },
  livré: { label: "Livré", icon: CheckCircle, color: "text-green-700 bg-green-100 border-green-300", desc: "" },
  annulé: { label: "Annulé", icon: XCircle, color: "text-red-600 bg-red-50 border-red-200", desc: "" },
};

// Machine à états — transitions autorisées pour le vendeur
const VENDOR_NEXT_STATES = {
  initiated: [
    { value: "en_livraison", label: "En livraison", danger: false },
    { value: "annulé", label: "Annuler", danger: true },
  ],
  payment_declared: [
    { value: "en_livraison", label: "En livraison", danger: false },
    { value: "annulé", label: "Annuler", danger: true },
  ],
  payment_confirmed: [
    { value: "en_livraison", label: "En livraison", danger: false },
    { value: "annulé", label: "Annuler", danger: true },
  ],
  approved: [{ value: "paid", label: "Marquer Payé", danger: false }],
  paid: [
    { value: "en_livraison", label: "En livraison", danger: false },
    { value: "annulé", label: "Annuler", danger: true },
  ],
  en_livraison: [
    { value: "livré", label: "Livré !", danger: false },
    { value: "annulé", label: "Annuler", danger: true },
  ],
};
const ADMIN_WA = "33777369462";

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [messages, setMessages] = useState({});   // { orderId: [] }
  const [msgInput, setMsgInput] = useState({});   // { orderId: "" }
  const [chatOpen, setChatOpen] = useState({});   // { orderId: bool }
  const [qualifying, setQualifying] = useState({}); // { orderId: bool }
  const [confirming, setConfirming] = useState({}); // { orderId: bool }
  const [sending, setSending] = useState({});
  const pollRef = useRef(null);
  const { user } = useAuth();

  useEffect(() => {
    API.get("/api/orders").then((r) => setOrders(r.data || [])).catch(console.error).finally(() => setLoading(false));
  }, []);

  const fetchMessages = useCallback(async (orderId) => {
    try {
      const { data } = await API.get(`/api/orders/${orderId}/messages`);
      setMessages((prev) => ({ ...prev, [orderId]: data || [] }));
    } catch (_) {}
  }, []);

  const openChat = (orderId) => {
    setChatOpen((prev) => {
      const next = !prev[orderId];
      if (next) {
        fetchMessages(orderId);
        pollRef.current = setInterval(() => fetchMessages(orderId), 10000);
      } else {
        clearInterval(pollRef.current);
      }
      return { ...prev, [orderId]: next };
    });
  };

  useEffect(() => () => clearInterval(pollRef.current), []);

  const sendMessage = async (orderId) => {
    const content = msgInput[orderId]?.trim();
    if (!content) return;
    setSending((p) => ({ ...p, [orderId]: true }));
    try {
      const { data } = await API.post(`/api/orders/${orderId}/messages`, { content });
      setMessages((prev) => ({ ...prev, [orderId]: [...(prev[orderId] || []), data] }));
      setMsgInput((p) => ({ ...p, [orderId]: "" }));
    } catch (_) {} finally {
      setSending((p) => ({ ...p, [orderId]: false }));
    }
  };

  const qualifyOrder = async (orderId) => {
    setQualifying((p) => ({ ...p, [orderId]: true }));
    try {
      await API.put(`/api/orders/${orderId}/qualify`);
      setOrders((prev) => prev.map((o) => o._id === orderId ? { ...o, is_qualified: true } : o));
    } catch (_) {} finally {
      setQualifying((p) => ({ ...p, [orderId]: false }));
    }
  };

  const confirmPayment = async (orderId) => {
    setConfirming((p) => ({ ...p, [orderId]: true }));
    try {
      await API.put(`/api/orders/${orderId}/confirm-payment`);
      const { data } = await API.get("/api/orders");
      setOrders(data || []);
    } catch (_) {} finally {
      setConfirming((p) => ({ ...p, [orderId]: false }));
    }
  };

  const updateStatus = async (orderId, status) => {
    await API.put(`/api/orders/${orderId}/status`, { status });
    setOrders((prev) => prev.map((o) => o._id === orderId ? { ...o, status } : o));
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-green-600 border-t-transparent" />
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen pb-24 md:pb-8">
      <div className="w-full max-w-2xl mx-auto px-4 py-5">
        <h1 className="text-xl font-extrabold text-gray-900 mb-1" data-testid="orders-title">
          {user?.role === "vendor" ? "Demandes reçues" : "Mes demandes de contact"}
        </h1>
        <p className="text-sm text-gray-500 mb-5">
          {user?.role === "vendor"
            ? "Les clients qui souhaitent vous contacter"
            : "Vos demandes de contact auprès des vendeurs"}
        </p>

        {orders.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <Package size={56} className="text-gray-200 mx-auto mb-3" />
            <p className="text-gray-500 font-semibold">
              {user?.role === "vendor" ? "Aucune demande reçue" : "Aucune demande envoyée"}
            </p>
            {user?.role !== "vendor" && (
              <Link to="/catalogue" className="mt-4 inline-block bg-green-600 text-white font-bold px-5 py-2 rounded-xl hover:bg-green-700 text-sm">
                Explorer les offres
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => {
              const statusCfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
              const StatusIcon = statusCfg.icon;
              const isApproved = ["payment_confirmed", "approved", "paid", "en_livraison", "livré"].includes(order.status);
              const vendorContactData = order.vendor_contact || order.vendor_info || {};
              const isExpanded = expandedOrder === order._id;

              // Build WhatsApp link (only when payment confirmed/approved and vendor WhatsApp available)
              const waLink = isApproved && vendorContactData?.whatsapp_number
                ? `https://wa.me/${formatWhatsApp(vendorContactData.whatsapp_number)}?text=${encodeURIComponent(`Bonjour, ma demande Jokkoleen #${order._id?.slice(-6).toUpperCase()}`)}` : null;

              return (
                <div key={order._id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm" data-testid="order-item">
                  {/* Header */}
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer"
                    onClick={() => setExpandedOrder(isExpanded ? null : order._id)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-extrabold text-gray-900 text-sm">
                          #{order._id?.slice(-6).toUpperCase()}
                        </p>
                        {order.is_contact_request && (
                          <span className="text-[10px] bg-green-100 text-green-700 font-bold px-1.5 py-0.5 rounded">Contact</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border flex items-center gap-1 ${statusCfg.color}`}>
                          <StatusIcon size={10} /> {statusCfg.label}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(order.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-black text-gray-900 text-sm">{formatPrice(order.total_amount)}</span>
                      <ChevronDown size={16} className={`text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                    </div>
                  </div>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 p-4 space-y-3">
                      {/* Items */}
                      {order.items?.map((item, i) => (
                        <div key={i} className="flex justify-between text-sm">
                          <span className="text-gray-600 truncate flex-1 mr-2">{item.name} ×{item.quantity}</span>
                          <span className="font-bold text-gray-900 shrink-0">{formatPrice(item.price * item.quantity)}</span>
                        </div>
                      ))}

                      {/* Status message */}
                      {statusCfg.desc && (
                        <div className={`text-xs rounded-xl p-3 border ${statusCfg.color}`}>
                          <StatusIcon size={12} className="inline mr-1" />{statusCfg.desc}
                        </div>
                      )}

                      {/* APPROVED/PAYMENT_CONFIRMED: show contact info */}
                      {isApproved && user?.role === "customer" && (
                        <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                          <p className="text-xs font-bold text-green-800 mb-2 flex items-center gap-1">
                            <ShieldCheck size={12} /> Coordonnées débloquées
                          </p>
                          {vendorContactData?.shop_name && (
                            <p className="text-sm font-semibold text-gray-800 mb-1">{vendorContactData.shop_name}</p>
                          )}
                          {vendorContactData?.phone_wave && (
                            <p className="text-xs text-gray-600 flex items-center gap-1.5 mb-1">
                              <div className="w-4 h-4 rounded-full bg-[#1CBBEE] flex items-center justify-center"><span className="text-white text-[8px] font-black">W</span></div>
                              Wave: <span className="font-bold">{vendorContactData.phone_wave}</span>
                            </p>
                          )}
                          {vendorContactData?.phone_orange && (
                            <p className="text-xs text-gray-600 flex items-center gap-1.5 mb-1">
                              <div className="w-4 h-4 rounded-full bg-[#FF6600] flex items-center justify-center"><span className="text-white text-[8px] font-black">O</span></div>
                              Orange Money: <span className="font-bold">{vendorContactData.phone_orange}</span>
                            </p>
                          )}
                          {waLink && (
                            <a href={waLink} target="_blank" rel="noopener noreferrer"
                              className="mt-2 flex items-center gap-1.5 bg-[#25D366] text-white text-xs font-bold px-3 py-2 rounded-lg hover:bg-[#20BD5A] w-fit"
                              data-testid="approved-whatsapp-btn">
                              <MessageCircle size={12} /> Contacter sur WhatsApp
                            </a>
                          )}
                        </div>
                      )}

                      {/* AWAITING_PAYMENT: show payment info and confirm button */}
                      {order.status === "awaiting_payment" && user?.role === "customer" && (
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                          <p className="text-xs font-bold text-blue-800 mb-2 flex items-center gap-1">
                            <AlertCircle size={12} /> Effectuez votre paiement
                          </p>
                          {order.vendor_payment_info?.shop_name && (
                            <p className="text-sm font-semibold text-gray-800 mb-2">{order.vendor_payment_info.shop_name}</p>
                          )}
                          {order.vendor_payment_info?.phone_wave && (
                            <p className="text-xs text-gray-600 flex items-center gap-1.5 mb-1">
                              <span className="w-4 h-4 rounded-full bg-[#1CBBEE] flex items-center justify-center text-white text-[8px] font-black shrink-0">W</span>
                              Wave: <span className="font-bold">{order.vendor_payment_info.phone_wave}</span>
                            </p>
                          )}
                          {order.vendor_payment_info?.phone_orange && (
                            <p className="text-xs text-gray-600 flex items-center gap-1.5 mb-1">
                              <span className="w-4 h-4 rounded-full bg-[#FF6600] flex items-center justify-center text-white text-[8px] font-black shrink-0">O</span>
                              Orange Money: <span className="font-bold">{order.vendor_payment_info.phone_orange}</span>
                            </p>
                          )}
                          <button
                            onClick={() => confirmPayment(order._id)}
                            disabled={confirming[order._id]}
                            className="mt-3 w-full flex items-center justify-center gap-2 bg-green-600 text-white font-bold py-2 rounded-xl text-xs hover:bg-green-700 disabled:opacity-60 transition-colors"
                            data-testid="confirm-payment-btn"
                          >
                            {confirming[order._id] ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><CheckCircle size={12} /> J'ai payé — Débloquer le contact</>}
                          </button>
                        </div>
                      )}

                      {/* PENDING: locked state (ancien flux admin) */}
                      {order.status === "pending" && user?.role === "customer" && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2">
                          <Lock size={14} className="text-amber-500 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs font-bold text-amber-800">En attente de validation</p>
                            <p className="text-xs text-amber-600">Les coordonnées du vendeur seront débloquées après validation par notre équipe.</p>
                          </div>
                        </div>
                      )}

                      {/* VENDOR: Lead blocked — pay-per-lead */}
                      {user?.role === "vendor" && order.vendor_blocked && order.status !== "pending" && (
                        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3" data-testid="blocked-lead-box">
                          <p className="text-xs font-bold text-orange-700 flex items-center gap-1 mb-1">
                            <Lock size={12} /> Lead bloqué — Quota FREE dépassé
                          </p>
                          <p className="text-xs text-orange-600 mb-2">Débloquez ce lead pour 200 FCFA via Wave/Orange Money</p>
                          <a
                            href={`https://wa.me/${ADMIN_WA}?text=${encodeURIComponent(`Bonjour Jokkoleen, je souhaite débloquer le lead #${order._id?.slice(-6).toUpperCase()} pour 200 FCFA.`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-orange-600"
                            data-testid="unlock-lead-btn"
                          >
                            Débloquer ce lead (200 FCFA)
                          </a>
                        </div>
                      )}

                      {/* VENDOR: Validation en cours pour les orders PENDING (ancien flux admin) */}
                      {user?.role === "vendor" && order.status === "pending" && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-2" data-testid="vendor-pending-notice">
                          <Lock size={14} className="text-amber-500 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-xs font-bold text-amber-800">Validation Jokkoleen en cours</p>
                            <p className="text-xs text-amber-600">Les coordonnées du client seront disponibles après validation par notre équipe.</p>
                          </div>
                        </div>
                      )}
                      {/* VENDOR: En attente du paiement client — affiche contact si non bloqué */}
                      {user?.role === "vendor" && ["awaiting_payment", "initiated"].includes(order.status) && (
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3" data-testid="vendor-awaiting-payment-notice">
                          <p className="text-xs font-bold text-blue-800 mb-2 flex items-center gap-1">
                            <Clock size={12} className="text-blue-500" />
                            {order.status === "initiated" ? "Client a saisi son numéro — paiement attendu" : "En attente du paiement client"}
                          </p>
                          {!order.vendor_blocked && order.customer_phone && (
                            <>
                              <p className="text-xs text-gray-600 mb-1">{order.customer_name}</p>
                              <p className="text-xs text-gray-600 flex items-center gap-1 mb-2">
                                <Phone size={10} /> {order.customer_phone}
                              </p>
                              <a
                                href={`https://wa.me/${formatWhatsApp(order.customer_phone)}?text=${encodeURIComponent(`Bonjour, je vous contacte suite à votre demande Jokkoleen #${order._id?.slice(-6).toUpperCase()}`)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1.5 bg-[#25D366] text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-[#20BD5A] w-fit"
                                data-testid="whatsapp-customer-btn"
                              >
                                <MessageCircle size={11} /> WhatsApp client
                              </a>
                            </>
                          )}
                        </div>
                      )}

                      {/* VENDOR: Paiement déclaré — action requise */}
                      {user?.role === "vendor" && order.status === "payment_declared" && (
                        <div className="bg-teal-50 border border-teal-200 rounded-xl p-3" data-testid="vendor-payment-declared-notice">
                          <p className="text-xs font-bold text-teal-800 mb-2 flex items-center gap-1">
                            <ShieldCheck size={12} className="text-teal-500" /> Client a déclaré son paiement — vérifiez et expédiez !
                          </p>
                          {!order.vendor_blocked && order.customer_phone && (
                            <>
                              <p className="text-xs text-gray-600 mb-1">{order.customer_name}</p>
                              <p className="text-xs text-gray-600 flex items-center gap-1 mb-2">
                                <Phone size={10} /> {order.customer_phone}
                              </p>
                              <a
                                href={`https://wa.me/${formatWhatsApp(order.customer_phone)}?text=${encodeURIComponent(`Bonjour, j'ai bien reçu votre paiement pour la commande #${order._id?.slice(-6).toUpperCase()} sur Jokkoleen. Je prépare votre commande !`)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1.5 bg-[#25D366] text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-[#20BD5A] w-fit"
                                data-testid="whatsapp-customer-btn"
                              >
                                <MessageCircle size={11} /> WhatsApp client
                              </a>
                            </>
                          )}
                        </div>
                      )}

                      {/* CLIENT: Bouton "Confirmer ma demande" → QUALIFIED */}
                      {user?.role === "customer" && isApproved && !order.is_qualified && (
                        <button
                          onClick={() => qualifyOrder(order._id)}
                          disabled={qualifying[order._id]}
                          className="w-full flex items-center justify-center gap-2 bg-[#F59E0B] text-white font-bold py-3 rounded-2xl text-sm hover:bg-[#D97706] disabled:opacity-60 transition-all hover:-translate-y-0.5"
                          data-testid="qualify-order-btn"
                        >
                          {qualifying[order._id] ? <Loader size={14} className="animate-spin" /> : <UserCheck size={14} />}
                          Confirmer ma demande
                        </button>
                      )}
                      {user?.role === "customer" && order.is_qualified && (
                        <div className="flex items-center gap-2 text-xs text-green-700 font-semibold bg-green-50 rounded-xl px-3 py-2">
                          <CheckCircle size={12} /> Demande confirmée — le vendeur est notifié
                        </div>
                      )}

                      {/* VENDOR: Lead QUALIFIED — FREE vs PRO */}
                      {user?.role === "vendor" && order.is_qualified && !["pending", "awaiting_payment", "initiated"].includes(order.status) && (
                        order.vendor_blocked ? (
                          <div className="bg-orange-50 border border-orange-200 rounded-xl p-3" data-testid="blocked-qualified-lead">
                            <p className="text-xs font-bold text-orange-700 flex items-center gap-1 mb-1">
                              <Lock size={12} /> Lead qualifié — déblocage requis
                            </p>
                            <p className="text-xs text-orange-600 mb-2">Ce client a confirmé son intérêt. Débloquez pour 200 FCFA.</p>
                            <a
                              href={`https://wa.me/${ADMIN_WA}?text=${encodeURIComponent(`Bonjour Jokkoleen, je veux débloquer le lead qualifié #${order._id?.slice(-6).toUpperCase()} (200 FCFA via Wave/Orange Money numéro 77 338 90 95)`)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 bg-orange-500 text-white text-xs font-bold px-3 py-2 rounded-xl hover:bg-orange-600"
                              data-testid="unlock-qualified-btn"
                            >
                              <MessageCircle size={11} /> Débloquer ce client (200 FCFA)
                            </a>
                          </div>
                        ) : (
                          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3" data-testid="qualified-contact-box">
                            <p className="text-xs font-bold text-blue-800 mb-1 flex items-center gap-1">
                              <UserCheck size={12} /> Lead qualifié — contact client
                            </p>
                            <p className="text-xs text-gray-600">{order.customer_name}</p>
                            {order.customer_phone && (
                              <p className="text-xs text-gray-600 flex items-center gap-1">
                                <Phone size={10} /> {order.customer_phone}
                              </p>
                            )}
                            <a
                              href={`https://wa.me/${formatWhatsApp(order.customer_phone)}?text=${encodeURIComponent(`Bonjour ${order.customer_name}, je vous contacte suite à votre demande Jokkoleen #${order._id?.slice(-6).toUpperCase()}`)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 flex items-center gap-1.5 bg-[#25D366] text-white text-xs font-bold px-3 py-2 rounded-xl hover:bg-[#20BD5A] w-fit"
                              data-testid="whatsapp-qualified-btn"
                            >
                              <MessageCircle size={11} /> WhatsApp client
                            </a>
                          </div>
                        )
                      )}

                      {/* VENDOR: Lead non qualifié et non bloqué */}
                      {user?.role === "vendor" && !order.is_qualified && !order.vendor_blocked && !["pending", "awaiting_payment", "initiated"].includes(order.status) && (
                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3" data-testid="customer-contact-box">
                          <p className="text-xs font-bold text-blue-800 mb-1">Contact client</p>
                          <p className="text-xs text-gray-600">{order.customer_name}</p>
                          {order.customer_phone && (
                            <p className="text-xs text-gray-600 flex items-center gap-1">
                              <Phone size={10} /> {order.customer_phone}
                            </p>
                          )}
                          {order.notes && <p className="text-xs text-gray-500 mt-1 italic">"{order.notes}"</p>}
                          <a
                            href={`https://wa.me/${formatWhatsApp(order.customer_phone)}?text=${encodeURIComponent(`Bonjour, je vous contacte suite à votre demande Jokkoleen #${order._id?.slice(-6).toUpperCase()}`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 flex items-center gap-1.5 bg-[#25D366] text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-[#20BD5A] w-fit"
                            data-testid="whatsapp-customer-btn"
                          >
                            <MessageCircle size={11} /> WhatsApp client
                          </a>
                        </div>
                      )}

                      {/* VENDOR: Lead bloqué quota FREE (non qualifié) */}
                      {user?.role === "vendor" && order.vendor_blocked && !order.is_qualified && !["pending", "awaiting_payment", "initiated"].includes(order.status) && (
                        <div className="bg-orange-50 border border-orange-200 rounded-xl p-3" data-testid="blocked-lead-box">
                          <p className="text-xs font-bold text-orange-700 flex items-center gap-1 mb-1">
                            <Lock size={12} /> Lead bloqué — Quota FREE dépassé
                          </p>
                          <p className="text-xs text-orange-600 mb-2">Débloquez ce lead pour 200 FCFA via Wave/Orange Money</p>
                          <a
                            href={`https://wa.me/${ADMIN_WA}?text=${encodeURIComponent(`Bonjour Jokkoleen, je souhaite débloquer le lead #${order._id?.slice(-6).toUpperCase()} pour 200 FCFA.`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-orange-600"
                            data-testid="unlock-lead-btn"
                          >
                            Débloquer ce lead (200 FCFA)
                          </a>
                        </div>
                      )}

                      {/* Vendor status update — machine à états */}
                      {user?.role === "vendor" && !["pending", "awaiting_payment", "initiated"].includes(order.status) && (
                        <div className="flex flex-wrap gap-1.5 pt-1 border-t border-gray-100">
                          {(VENDOR_NEXT_STATES[order.status] || []).map(({ value, label, danger }) => (
                            <button
                              key={value}
                              onClick={() => updateStatus(order._id, value)}
                              className={`text-xs px-2.5 py-1 rounded-lg border font-semibold transition-colors ${
                                danger
                                  ? "bg-red-50 text-red-600 border-red-200 hover:bg-red-100"
                                  : "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                              }`}
                              data-testid={`update-status-${value}`}
                            >
                              {label}
                            </button>
                          ))}
                          {(VENDOR_NEXT_STATES[order.status] || []).length === 0 && (
                            <span className="text-xs text-gray-400 italic py-1">Commande terminée</span>
                          )}
                        </div>
                      )}

                      {/* ===== MESSAGERIE INTERNE ===== */}
                      {order.status !== "pending" && (
                        <div className="border-t border-gray-100 pt-3">
                          <button
                            onClick={() => openChat(order._id)}
                            className="flex items-center gap-2 text-xs font-semibold text-gray-500 hover:text-[#16A34A] transition-colors"
                            data-testid="toggle-chat-btn"
                          >
                            <MessageCircle size={13} />
                            {chatOpen[order._id] ? "Masquer messages" : `Messages ${messages[order._id]?.length > 0 ? `(${messages[order._id].length})` : ""}`}
                          </button>

                          {chatOpen[order._id] && (
                            <div className="mt-3">
                              {/* Messages list */}
                              <div className="space-y-2 max-h-48 overflow-y-auto mb-3 scrollbar-hide" data-testid="messages-list">
                                {(messages[order._id] || []).length === 0 ? (
                                  <p className="text-xs text-gray-400 text-center py-3">Aucun message. Commencez la conversation !</p>
                                ) : (messages[order._id] || []).map((msg) => {
                                  const isMine = msg.sender_id === user?._id || msg.sender_role === user?.role;
                                  return (
                                    <div key={msg._id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                                      <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-xs ${isMine ? "bg-[#16A34A] text-white rounded-br-sm" : "bg-gray-100 text-gray-800 rounded-bl-sm"}`}>
                                        <p className="font-semibold text-[10px] opacity-70 mb-0.5">
                                          {isMine ? "Moi" : msg.sender_name}
                                        </p>
                                        <p>{msg.content}</p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>

                              {/* Input */}
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={msgInput[order._id] || ""}
                                  onChange={(e) => setMsgInput((p) => ({ ...p, [order._id]: e.target.value }))}
                                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage(order._id)}
                                  placeholder="Votre message... (pas de numéros de téléphone)"
                                  className="flex-1 border border-gray-200 rounded-2xl px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#16A34A]"
                                  data-testid="message-input"
                                />
                                <button
                                  onClick={() => sendMessage(order._id)}
                                  disabled={sending[order._id] || !msgInput[order._id]?.trim()}
                                  className="bg-[#16A34A] text-white rounded-full p-2 hover:bg-[#166534] disabled:opacity-40 transition-colors"
                                  data-testid="send-message-btn"
                                >
                                  {sending[order._id] ? <Loader size={13} className="animate-spin" /> : <Send size={13} />}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
